// Configure.js

// React and Hooks
import React, { useMemo } from "react";

// Contexts
import { useEnvironments } from "../contexts/EnvironmentsContext.js";

// API Utilities
import { removeScreen, removeEnvironment } from "../utils/api.js";

// Utility Components/Functions
import Loading from "../utils/Loading";
import { useResponsive } from '../utils/useResponsive';

// Component Imports
import { ConfigureHeaderGrid } from './ConfigureHeaderGrid';
import { ConfigureEnvironmentScreen } from './ConfigureEnvironmentScreen';

// Main component
export function ConfigureDisplays(t) {

    console.log('Configure component is called');
    
    // Accessing context values using useEnvironments hook
    const { account, house, environments, setEnvironments } = useEnvironments();

    // Deriving environmentGroup from environments
    const environmentGroup = useMemo(() => {
        if (!environments || environments.length === 0) {
            return [];
        }
        return environments.map(env => ({
            environment: env,
            screens: env.screens || []
        }));
    }, [environments]);

    const handleRemoveScreen = (id) => {
        const screenToRemove = environments.flatMap(env => env.screens || []).find(screen => String(screen.id) === String(id));
        
        if (screenToRemove.isActive) {
            alert(t("configure.cannot.remove.screen"));
            return;
        }
    
        const environmentOfScreen = environments.find(env => env.screens.includes(screenToRemove));
        
        if (environmentOfScreen.name !== "Web player" && environmentOfScreen.screens.length === 1) {
            alert(t("configure.requires.one.screen"));
            return;
        }
    
        removeScreen(id)
            .then(() => {
                const newEnvironments = environments.map(env => {
                    if (env.id === environmentOfScreen.id) {
                        return {
                            ...env,
                            screens: env.screens.filter(screen => String(screen.id) !== String(id)),
                        };
                    }
                    return env;
                });
    
                setEnvironments(newEnvironments); // Update environments in context
            })
            .catch(error => {
                console.error(error);
            });
    };
    
    const handleRemoveEnvironment = (id) => {
        console.log("[Configure] handleRemoveEnvironment start:", id);
        
        // Find the environment to check if it's active
        const environmentToRemove = environments.find(env => String(env.id) === String(id));
        
        if (!environmentToRemove) {
            console.warn("[Configure] Environment not found:", id);
            return;
        }
        
        // Check if environment is active (alive === "1")
        if (environmentToRemove.alive === "1") {
            console.log("[Configure] Cannot remove active environment:", environmentToRemove);
            alert("configure.cannot.remove.env.active");
            return;
        }
        
        console.log("[Configure] Environment is not active, proceeding with removal");
        
        // Create filtered environments array
        const newEnvironments = environments.filter(env => String(env.id) !== String(id));
        console.log("[Configure] After filtering, newEnvironments:", newEnvironments);
        
        // Update the UI
        console.log("[Configure] Updating UI state");
        setEnvironments(newEnvironments);
        
        // Make API call
        console.log("[Configure] Starting API call");
        try {
            removeEnvironment(id);
            console.log("[Configure] removeEnvironment called");
        } catch (err) {
            console.error("[Configure] Error calling removeEnvironment:", err);
        }
        
        console.log("[Configure] handleRemoveEnvironment completed");
    };

    // Responsive design hooks
    const { isMobile, isTablet, isHD, isUHD, isSmartTV, isPortrait, isLandscape } = useResponsive()

    // Calculating numRows
    const numRows = environmentGroup.reduce((totalRows, environment) => {
        let environmentRows = 6;
        if (environment.screens.length >= 2) {
            environmentRows += (environment.screens.length - 1) * 5;
        }
        return totalRows + environmentRows;
    }, 14);
    //TODO: Check if still ok when removing screens/environments

    // Rendering
    return (
        <div
            className="grid-container"
            style={{
                display: "grid",
                minWidth: "360px",
                gridTemplateColumns: isMobile ? "2px 2px repeat(11, 1fr) 2px 2px" : "repeat(15, 1fr)",
                gridTemplateRows: `repeat(${numRows}, minmax(15px, 40px))`,
                overflow: "auto",
                alignItems: "stretch",
            }}
        >
            <ConfigureHeaderGrid account={{ data: account }} house={house} />
            <ConfigureEnvironmentScreen
                environmentGroup={environmentGroup}
                handleRemoveScreen={handleRemoveScreen}
                handleRemoveEnvironment={handleRemoveEnvironment}
            />
        </div>
    );
}